import {useCallback, useEffect, useMemo, useState} from "react";
import styles from "./LocationDetailsGeneral.module.scss";
import {Field} from "@progress/kendo-react-form";
import {
    CoordinatesMap,
    CoordinatesMapEdit,
    CustomTextFormField, DrawGeometryType,
    FormDropDown,
    FormInput, FormNumericTextBox, FormTimeZoneDropdown, GoogleAddressAutoCompleteInput,
    LookupTextFormField, requiredValidator,
    TextFormField,
    useConfirmation
} from "../../../common";
import {
    ICustomer,
    ICustomerLookupValue,
    ILocation,
    ILookupValue,
    LocationType
} from "../../../../model";
import {CheckBoxFormField} from "../../../common/forms/form-checkbox/CheckBoxFormField";
import {Point, Polygon} from "geojson";
import {geoUtils} from "../../../../utils";
import {customersService, locationsService, notifications} from "../../../../services";
import {connectedServicesService} from "../../../../services/api/connected-services";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useExtendedLocalization} from "../../../../hooks";
import {hasUmojoAnalyticsByUniqueId} from "../../../../model/api/nexParkInstance/instancesChecker";

const numberValidator = (value: number) => (!value || value % 1 === 0 ? '' : 'Invalid');

interface IProps {
    edit: boolean;
    location?: ILocation;
    formRenderProps?: any;
}

const LocationDetailsGeneral = (props: IProps) => {
    const {edit, formRenderProps} = props;
    const [location, setLocation] = useState<ILocation | undefined>(() => props.location);
    const [searchParams, setSearchParams] = useSearchParams();
    const customerLinkPath = props.location?.customer && `/customers/${props.location.customer.id}`;

    const navigate = useNavigate();
    const localization = useExtendedLocalization();

    const [linkedCustomer, setLinkedCustomer] = useState<ICustomer | null>(null)
    const [customers, setCustomers] = useState<ICustomerLookupValue[]>([]);
    const [customerConnectedServices, setCustomerConnectedServices] = useState<ILookupValue[]>([]);
    const [saving, setSaving] = useState(false);
    const confirmation = useConfirmation();
    const [isFromDetails, setIsFromDetails] = useState<boolean>(false);

    useEffect(() => {
        if (edit) {
            customersService.getLookupValues().then((allCustomers) => {
                const filteredCustomers = allCustomers.filter((customer) => {
                    const serviceIds = customer.connectedServiceUniqueIds || [];
                    return hasUmojoAnalyticsByUniqueId(serviceIds);
                });

                setCustomers(filteredCustomers);
            });
            if (location?.customer && location?.customer?.id) {
                connectedServicesService.getByCustomer(location.customer.id)
                    .then((res) => setCustomerConnectedServices(res));
                customersService.get(String(location.customer.id)).then((customer: ICustomer) => {
                    setLinkedCustomer(customer);
                })
            }
        }

        setIsFromDetails(searchParams.get('source') === 'details');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit]);

    const locationTypes = useMemo(
        () => Object.values(LocationType).map((x) => ({
            id: x,
            name: localization.toLanguageStringF(`location.types.${x}`)
        })),
        [localization],
    );

    const mapCenter: Point | any = useMemo(() => {
        switch (location?.geometry?.type) {
            case 'Point':
                return location.geometry as Point;
            case 'Polygon':
                return geoUtils.findCenter(location.geometry as Polygon);
            default:
                return {type: 'Point', coordinates: (linkedCustomer?.address?.geometry as any)?.coordinates ?? [-85.754, 38.2523]};
        }
    }, [location?.geometry, linkedCustomer]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onCustomerChange = (ev: any) => {
        connectedServicesService.getByCustomer(ev.value.id).then((res) => setCustomerConnectedServices(res));
    };

    const handleDeactivate = useCallback(
        async (event: any, onChange: (name: string, options: { value: any }) => void) => {
            const newValue = event.value;
            if (newValue) {
                return;
            }
            confirmation({
                title: 'Confirm deactivation of this Customer?',
                message:
                    'Please note that deactivating this item will not automatically disable associated services or devices such as cameras. These actions must be completed manually in the portal.',
            })
                .then(() => {
                    if (location) {
                        locationsService.deactivate(location.id);
                    }
                    onChange('enabled', {value: false});
                })
                .catch((err) => {
                    onChange('enabled', {value: true});
                });
        },
        [confirmation, location],
    );

    return (
        <div className={styles.panelBar}>
            <div className={styles.panel}>
                <div className={styles.stroke}>
                        <Field
                            name='name'
                            label='Name'
                            component={edit ? FormInput : TextFormField}
                            required={true}
                            validator={requiredValidator}
                        />

                    {edit ? (
                        <Field
                            name='type'
                            label='Type'
                            component={FormDropDown}
                            data={locationTypes}
                            valueMap={(value: { id: LocationType }) => value?.id}
                            dataItemKey='id'
                            textField='name'
                            required={true}
                            validator={requiredValidator}
                        />
                    ) : (
                        <Field
                            name='type'
                            label='Type'
                            component={CustomTextFormField}
                            customTextGetter={(v: LocationType) => locationTypes.find((x) => x.id === v)?.name}
                        />
                    )}

                    {formRenderProps.valueGetter('type') === LocationType.Garage && (
                        <Field
                            name='settings.garageSettings.floors'
                            label='Floors'
                            component={edit ? FormNumericTextBox : TextFormField}
                            validator={numberValidator}
                        />
                    )}
                </div>
                <div className={styles.stroke}>
                    <Field
                        name='capacity'
                        label='Capacity'
                        component={edit ? FormNumericTextBox : TextFormField}
                        validator={numberValidator}
                    />

                    <Field
                        name='customer'
                        label='Customer'
                        href={customerLinkPath}
                        component={edit ? FormDropDown : LookupTextFormField}
                        data={customers}
                        dataItemKey='id'
                        textField='name'
                        required={true}
                        validator={requiredValidator}
                        onChange={onCustomerChange}
                    />
                </div>
                <div className={styles.stroke}>
                    <Field
                        name={'enabled'}
                        label={'Status'}
                        edit={edit}
                        secondLabel={'Active'}
                        onChange={edit ? (event) => handleDeactivate(event, formRenderProps.onChange) : undefined}
                        component={CheckBoxFormField}
                    />
                </div>

                <div className={styles.stroke}>
                    {edit ? (
                        <Field
                            name='address'
                            label='Address'
                            component={GoogleAddressAutoCompleteInput}
                            required={true}
                            validator={requiredValidator}
                            onPlaceSelected={(address: { position: [number, number] }) => {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                const type: any = location?.geometry?.type || 'Point';

                                if (address?.position && address.position[0] && address.position[0] && location) {
                                    formRenderProps.onChange('geometry', {
                                        value: {
                                            type: type,
                                            coordinates: address.position,
                                        },
                                    });
                                    setLocation({
                                        ...location,
                                        ...{
                                            geometry: {
                                                type: type,
                                                coordinates: address.position,
                                            },
                                        },
                                    });
                                }
                            }}
                        />
                    ) : (
                        <Field name='address' label='Address' component={TextFormField}/>
                    )}

                    <FormTimeZoneDropdown name='timezone' label='Time Zone' edit={edit} required={true}/>
                </div>

                <div className={styles.stroke}>
                    <div className='k-form-field' style={{width: '100%'}}>
                        {edit && <span className={styles.polygoneInfo}>Draw polygon to define the exact location and borders</span>}
                        <div className='k-form-field-wrap'>
                            {edit ? (
                                <Field
                                    key="coordinates-map-edit"
                                    component={CoordinatesMapEdit}
                                    name='geometry'
                                    required={true}
                                    validator={requiredValidator}
                                    geometryType={
                                        formRenderProps.valueGetter('geometry')?.type === 'Point'
                                            ? DrawGeometryType.Point
                                            : DrawGeometryType.Polygon
                                    }
                                    center={mapCenter}
                                />
                            ) : (
                                location && <CoordinatesMap geometry={location?.geometry}/>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default LocationDetailsGeneral;
